import type { BlockType } from '@unifyapps/defs/types/block';
import type { JavaScriptFunction } from '@unifyapps/defs/types/javascriptFunction';
import type { DependencyGraphManager } from '../../../../../dependency/manager/DependencyGraphManager';
import { buildBlockImplicitDependencies } from '../../../../dependency/buildBlockImplicitDependencies';
import { buildPageFunctionImplicitDependencies } from '../../../../dependency/buildPageFunctionImplicitDepndencies';
import InterfacePageHelper from '../../../../helper/InterfacePage';
import type { UnsavedDataSource } from '../../../../stores/DataSourceRecordStore';
import type BlocksRegistry from '../../../RegistryProvider/BlocksRegistry';
import { dataSourceDependencyBuilder, pageFieldDependencyBuilder } from '../../builders';
import { PAGE_FIELD_KEYS_TO_TRACK } from '../../const';
import type { EntityListType } from '../../types';

// ---- shared passes (both saved-deps and rebuild paths run these) --------------

export function buildEntityDataSourceDependencyGraph({
  entityDataSources,
  graphManager,
}: {
  entityDataSources: Record<string, UnsavedDataSource | undefined>;
  graphManager: DependencyGraphManager;
}) {
  const entries = Object.entries(entityDataSources);
  for (
    let entityDataSourceIndex = 0;
    entityDataSourceIndex < entries.length;
    entityDataSourceIndex++
  ) {
    const [, entityDataSource] = entries[entityDataSourceIndex];
    if (!entityDataSource) continue;
    const dataSourceDependencyGraph = dataSourceDependencyBuilder({ dataSource: entityDataSource });
    graphManager.updateNodes(dataSourceDependencyGraph);
  }
}

export function buildBlocksImplicitDependencies({
  blocks,
  graphManager,
  interfacePageId,
  registry,
}: {
  blocks: Record<string, BlockType | undefined>;
  graphManager: DependencyGraphManager;
  interfacePageId: string;
  registry: BlocksRegistry;
}) {
  const blockValues = Object.values(blocks);
  for (let blockIndex = 0; blockIndex < blockValues.length; blockIndex++) {
    const block = blockValues[blockIndex];
    if (!block) continue;
    const deps = buildBlockImplicitDependencies({ block, registry, interfacePageId });
    graphManager.mergeNode(block.id, deps);
  }
}

export function buildPageFunctionsImplicitDependencies({
  pageFunctions,
  graphManager,
}: {
  pageFunctions: Record<string, JavaScriptFunction | undefined>;
  graphManager: DependencyGraphManager;
}) {
  const pageFunctionValues = Object.values(pageFunctions);
  for (
    let pageFunctionIndex = 0;
    pageFunctionIndex < pageFunctionValues.length;
    pageFunctionIndex++
  ) {
    const pageFunction = pageFunctionValues[pageFunctionIndex];
    if (!pageFunction) continue;
    const deps = buildPageFunctionImplicitDependencies({ pageFunction });
    graphManager.mergeNode(pageFunction.id, deps);
  }
}

// rebuild page-level field deps (e.g. documentTitle) from live entityList values.
// used by buildNewDependencies and as a fallback when no saved record exists.
export function buildPageFieldDependencies({
  entityList,
  graphManager,
}: {
  entityList: EntityListType;
  graphManager: DependencyGraphManager;
}) {
  Object.values(PAGE_FIELD_KEYS_TO_TRACK).forEach((fieldKey) => {
    const fieldValue = entityList[fieldKey as keyof Pick<EntityListType, 'documentTitle'>];
    const pageFieldDependency = pageFieldDependencyBuilder({ fieldKey, fieldValue });
    graphManager.updateNodes(pageFieldDependency);
  });
}

// when shouldUseBuiltDependencies is true, prefer the saved dependency records
// on the page instead of recomputing. fall back to the rebuild path when no
// saved deps are present so documentTitle still lands in the graph.
export function applySavedPageFieldDependencies({
  entityList,
  graphManager,
}: {
  entityList: EntityListType;
  graphManager: DependencyGraphManager;
}) {
  const savedDeps = entityList.pageFieldDependencies;
  if (!savedDeps || Object.keys(savedDeps).length === 0) {
    buildPageFieldDependencies({ entityList, graphManager });
    return;
  }
  const fieldEntries = Object.entries(savedDeps);
  for (let fieldIndex = 0; fieldIndex < fieldEntries.length; fieldIndex++) {
    const [fieldKey, fieldDependency] = fieldEntries[fieldIndex];
    if (!fieldDependency) continue;
    graphManager.updateNode(fieldKey, InterfacePageHelper.getDependencyDependsOn(fieldDependency));
  }
}
